/**
 * DataIndex — a query-friendly view over numeric-series cells
 * built through the preset adapter machinery.
 *
 * Built lazily via `row.as(DATA_FACADE)`. On first access (or
 * after a `ColumnDataChanged` invalidation) the index:
 *   1. Resolves the preset from `row.lookupKey` via `DATA_PRESETS`
 *      (falls back to a `[*]`-wildcard lookup for parameterized
 *      keys like `:~garmin:auth_abc:stress`).
 *   2. Derives `Graph.Props` from the preset tree via
 *      `propsFromNodes`.
 *   3. Runs each series' adapter over the row's cells, collecting
 *      `[x, y]` points.
 *
 * Queries expose points per series with binary-search range
 * filtering. `peekDataFrame(range, tz)` produces a column-scoped
 * DataFrame suitable for a renderer to paint.
 *
 * Custom-cell DTR-keyed payloads don't exist in tento/tento-lona-js/sheets —
 * all cells carry `NaiveDate.Partial`. Adapters receive an empty
 * `customCells` stream; group-adapter presets that rely on DTR
 * values degrade gracefully to standard-cell input.
 */

import type { Option } from "../core/option.ts";
import type { NaiveDate, DateTime, TimezoneRegion, Utc } from "@tento-chrono";
import { Tracing } from "@tento-core/tracing";

import type { CellEntry, CellValue } from "../core/dtype.ts";
import type { SheetRegistries } from "../sheet/sheet-registries.ts";
import {
  propsFromNodes,
  type GraphSpecNode,
} from "./data/graph-props.ts";
import type { Graph, PointY } from "../stores/graph-types.ts";
import type * as GraphTypes from "../stores/graph-types.ts";
import * as GraphProps from "./data/graph-props.ts";
import type { TimepointCellEntry } from "./data/series.ts";
import { elementAt } from "../core/checked-index.ts";

/** The data index's counters from its last populate: cells probed and points produced. */
export interface DataIndexDiagnostics {
  /** Total cells fed into `populate` most recently. */
  probed: number;
  /** Points produced by the adapters. */
  accepted: number;
  /** Cells whose adapter call returned null or [] (no points). */
  dropped: number;
}

/** The index behind `DataFacade`: a row's cells decoded into typed series. */
export class DataIndex {
  readonly #seriesById = new Map<string, DataIndex.Series>();
  #graphPropsCache: Option<Graph.Props> = null;
  #diagnosticCounts: DataIndexDiagnostics = {
    probed: 0,
    accepted: 0,
    dropped: 0,
  };

  /**
   * Rebuild the index from the preset `registries` holds for `lookupKey`
   * (the sheet's `Sheet.registries`). The chart config (yAxis, series,
   * renderers) is the preset's. A null lookupKey or an
   * unregistered key yields an empty index — the caller may instead call
   * `populateFromNodes` with a node tree derived from the sheet's own rows
   * (the user-level, no-preset path).
   */
  populate(
    registries: SheetRegistries,
    lookupKey: Option<string>,
    cells: readonly CellEntry[],
    customCells: readonly TimepointCellEntry[],
  ): void {
    const specs = lookupKey ? registries.dataPresets.get(lookupKey) : null;
    this.populateFromNodes(specs ?? null, cells, customCells);
  }

  /**
   * Rebuild the index from an explicit chart-config node tree (yAxis /
   * data-series / renderer nodes) instead of the preset registry. This is
   * the generic seam: presets and the sheet's own rows both reduce to a
   * `GraphSpecNode[]` here (`propsFromNodes` walks either). `null` nodes →
   * empty index (no preset, no rows-driven config).
   */
  populateFromNodes(
    nodes: Option<readonly GraphSpecNode[]>,
    cells: readonly CellEntry[],
    customCells: readonly TimepointCellEntry[],
  ): void {
    this.#seriesById.clear();

    if (!nodes) {
      this.#graphPropsCache = null;
      this.#diagnosticCounts = { probed: cells.length, accepted: 0, dropped: 0 };
      return;
    }

    const props = propsFromNodes(nodes);
    this.#graphPropsCache = props;

    let totalAccepted = 0;
    let totalDropped = 0;
    for (const spec of props.series) {
      const { points, accepted, dropped } = DataIndex.#extractPoints(
        spec,
        cells,
        customCells,
      );
      points.sort((a, b) => a[0] - b[0]);
      totalAccepted += accepted;
      totalDropped += dropped;

      this.#seriesById.set(spec.key, {
        key: spec.key,
        type: spec.type,
        label: spec.label,
        points,
        yRange: spec.yRange ?? null,
        convHalfWidth: spec.convHalfWidth ?? null,
        maxGap: spec.maxGap ?? null,
        minPoints: spec.minPoints ?? null,
        minCoverage: spec.minCoverage ?? null,
      });
    }
    this.#diagnosticCounts = {
      probed: cells.length,
      accepted: totalAccepted,
      dropped: totalDropped,
    };

    // One-shot per populate — what the config + extraction produced: how many
    // series/renderers the node tree yielded, and per-series point counts. Zero
    // series or all-zero points here pinpoints an extraction/formula break
    // downstream of a resolved config.
    Tracing.log("rows-chart/populated", {
      seriesCount: props.series.length,
      rendererCount: props.renderers.length,
      cellCount: cells.length,
      accepted: totalAccepted,
      points: [...this.#seriesById.values()].map((s) =>
        `${s.key}:${s.points.length}`
      ),
    });
  }

  // ── Queries ────────────────────────────────────────────────────────

  /** Resolved Graph.Props from the preset, or null if no preset found. */
  get graphProps(): Option<Graph.Props> {
    return this.#graphPropsCache;
  }

  /** Get a single series by key. */
  peekSeries(key: string): Option<DataIndex.Series> {
    return this.#seriesById.get(key) ?? null;
  }

  /**
   * The points of `key`'s series within an x-range `[start, end)` whose `Y`
   * has `key`'s shape (`DataIndex.SeriesKey.scalar("default")` → numbers).
   */
  peekPointsInRange<Y extends PointY>(
    key: DataIndex.SeriesKey<Y>,
    start: number,
    end: number,
  ): readonly [number, Y][] {
    const s = this.#seriesById.get(key.id);
    if (!s || s.points.length === 0) return [];

    let lo = 0;
    let hi = s.points.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (elementAt(s.points, mid, "data-index: s.points")[0] < start) lo = mid + 1;
      else hi = mid;
    }

    const result: [number, Y][] = [];
    for (let i = lo; i < s.points.length; i++) {
      const [x, y] = elementAt(s.points, i, "DataIndex: points");
      if (x >= end) break;
      if (key.probe(y)) result.push([x, y]);
    }
    return result;
  }

  /** Get all series. */
  peekAllSeries(): ReadonlyMap<string, DataIndex.Series> {
    return this.#seriesById;
  }

  diagnostics(): DataIndexDiagnostics {
    return { ...this.#diagnosticCounts };
  }

  /**
   * Build a DataFrame for a specific column. Mirrors lona-js's
   * `peekDataFrame` — pure data, no DOM.
   */
  peekDataFrame(
    range: NaiveDate.Range,
    tz: TimezoneRegion,
  ): Option<DataIndex.DataFrame> {
    if (!this.#graphPropsCache) return null;

    const toXRange = this.#graphPropsCache.toXRange;
    const xRange = toXRange
      ? toXRange(range, tz)
      : { start: range.start.dse, end: range.end.dse };

    const series = new Map<string, DataIndex.DataFrameSeries>();
    for (const [key, s] of this.#seriesById) {
      const points = this.peekPointsInRange(
        DataIndex.SeriesKey.any(key),
        xRange.start,
        xRange.end,
      );
      series.set(key, {
        key,
        type: s.type,
        label: s.label,
        points,
        yRange: s.yRange,
        convHalfWidth: s.convHalfWidth,
        maxGap: s.maxGap,
        minPoints: s.minPoints,
        minCoverage: s.minCoverage,
      });
    }

    return {
      xRange,
      yAxis: this.#graphPropsCache.yAxis,
      series,
      rendererSpecs: this.#graphPropsCache.renderers,
    };
  }

  // ── Internal ──────────────────────────────────────────────────────

  static #extractPoints(
    spec: Graph.SeriesSpec,
    cells: readonly CellEntry[],
    customCells: readonly TimepointCellEntry[],
  ): { points: [number, PointY][]; accepted: number; dropped: number } {
    const points: [number, PointY][] = [];
    let accepted = 0;
    let dropped = 0;

    if (spec.groupAdapter) {
      const data: [NaiveDate.Partial, NonNullable<CellValue>][] = [];
      const dataCustom: [DateTime.Range<Utc>, NonNullable<CellValue>][] = [];
      for (const c of cells) {
        if (c.value != null) data.push([c.date, c.value]);
      }
      for (const c of customCells) {
        if (c.value != null) dataCustom.push([c.dtr, c.value]);
      }
      const result = spec.groupAdapter(data, dataCustom);
      if (result) {
        for (const [x, y] of result) {
          if (y != null) {
            points.push([x, y]);
            accepted++;
          } else {
            dropped++;
          }
        }
      }
    } else if (spec.adapter) {
      const adapter = spec.adapter;
      for (const c of cells) {
        if (c.value == null) {
          dropped++;
          continue;
        }
        const xys = adapter(c.date, null, c.value);
        if (!xys || xys.length === 0) {
          dropped++;
          continue;
        }
        for (const [x, y] of xys) {
          if (y != null) {
            points.push([x, y]);
            accepted++;
          } else {
            dropped++;
          }
        }
      }
      for (const c of customCells) {
        if (c.value == null) continue;
        const xys = adapter(null, c.dtr, c.value);
        if (!xys) continue;
        for (const [x, y] of xys) {
          if (y != null) {
            points.push([x, y]);
            accepted++;
          }
        }
      }
    }

    return { points, accepted, dropped };
  }
}

/**
 * Types and values that go with `DataIndex`: `SeriesKey`, `Point`, `OhlcY`,
 * `BarWidthY`, `RecordY`, `Series`, and 2 more.
 */
export namespace DataIndex {
  /** A series key and the point shape read from it; see `SeriesKey`. */
  export const SeriesKey: typeof GraphProps.SeriesKey = GraphProps.SeriesKey;
  export type SeriesKey<Y extends PointY> = GraphProps.SeriesKey<Y>;
  /** A point's `Y`: what a series' adapter produced for one `x`. */
  export type Point = PointY;
  /** A candlestick point's `Y`. */
  export type OhlcY = GraphTypes.OhlcY;
  /** A `[value, width]` bar point's `Y`. */
  export type BarWidthY = GraphTypes.BarWidthY;
  /** A record point's `Y` (read field by field by its renderer). */
  export type RecordY = GraphTypes.RecordY;

  export type Series = {
    readonly key: string;
    readonly type: "regression" | "default";
    readonly label: Option<string>;
    readonly points: readonly [number, PointY][];
    readonly yRange: Option<{ min: number; max: number; center: number }>;
    readonly convHalfWidth: Option<number>;
    readonly maxGap: Option<number>;
    readonly minPoints: Option<number>;
    readonly minCoverage: Option<number>;
  };

  /** Column-scoped data frame — pure data, no DOM. */
  export type DataFrame = {
    readonly xRange: { start: number; end: number };
    readonly yAxis: Option<{ min: number; max: number; center: number }>;
    readonly series: ReadonlyMap<string, DataFrameSeries>;
    readonly rendererSpecs: readonly Graph.RendererSpec[];
  };

  export type DataFrameSeries = {
    readonly key: string;
    readonly type: "regression" | "default";
    readonly label: Option<string>;
    readonly points: readonly [number, PointY][];
    readonly yRange: Option<{ min: number; max: number; center: number }>;
    readonly convHalfWidth: Option<number>;
    readonly maxGap: Option<number>;
    readonly minPoints: Option<number>;
    readonly minCoverage: Option<number>;
  };
}
